import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import ConvertDDMMYYYYtoISO from '../../shared-functions/ConvertDDMMYYYYtoISO';
import ConvertUTCtoDateTime from '../../shared-functions/ConvertUTCtoDateTime';
import { permissionCheck } from '../../shared-functions/JWTDocodeFunction';
import tariffApi from '../../utils/api/tariff';
import BatteryLoader from '../battery-loader/BatteryLoader';
import MyButton from '../button/Button';

interface TariffSidePanelProperties {
  /** Source to fetch the tariff ID from ('queryParams' or 'props') */
  fetchIdFrom?: 'queryParams' | 'props';
  /** Tariff ID passed as a prop */
  id?: string;
  /** Flag indicating if the panel is opened from the Partner page */
  isPartner?: boolean;
}

/**
 * Tariff side panel component to display detailed information for a selected tariff
 * @param {TariffSidePanelProperties} properties - Properties for TariffSidePanel component
 * @returns {React.JSX.Element} TariffSidePanel component
 */
function TariffSidePanel(properties: TariffSidePanelProperties = {}): React.JSX.Element {
  const { fetchIdFrom = 'queryParams', id, isPartner = false } = properties;
  const [searchParameters] = useSearchParams();
  const navigate = useNavigate();
  const { id: parameterId } = useParams<{ id: string }>();

  const tariffId =
    fetchIdFrom === 'props'
      ? id || searchParameters.get('tariffId') || parameterId
      : searchParameters.get('tariffId') || id || parameterId;

  const [tariffDetails, setTariffDetails] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (tariffId) {
      setLoading(true);
      tariffApi
        .fetchSingleTariff(tariffId, isPartner)
        .then((response: any) => {
          const rawScopeLevels = response?.data?.scopeLevels;
          let parsedScopeLevels: any[] = [];
          if (Array.isArray(rawScopeLevels)) {
            parsedScopeLevels = rawScopeLevels;
          } else if (typeof rawScopeLevels === 'string') {
            try {
              parsedScopeLevels = JSON.parse(rawScopeLevels);
            } catch {
              parsedScopeLevels = [];
            }
          }

          const output = {
            status: response?.data?.status,
            enforcementDate: ConvertUTCtoDateTime(response?.data?.effectiveFrom),
            expiryDate: ConvertUTCtoDateTime(response?.data?.effectiveTo),
            tariffName: response?.data?.name,
            tariffType: response?.data?.type,
            currentType:
              response?.data?.currentType || response?.data?.connectorRating?.currentType,
            capacityKW: response?.data?.connectorRating?.capacityKW ?? response?.data?.capacityKW,
            unit: response?.data?.connectorRating?.unit ?? response?.data?.unit,
            accountCustomId: response?.data?.accountCustomId,
            connectorRating: response?.data?.connectorRating,
            scopeLevels: response?.data?.scopeLevels,
            country: parsedScopeLevels?.find((level: any) => level.scope === 'country')?.scopeName,
            state: parsedScopeLevels?.find((level: any) => level.scope === 'state')?.scopeName,
            city: parsedScopeLevels?.find((level: any) => level.scope === 'city')?.scopeName,
            stationId: response?.data?.stationId,
            accountName: response?.data?.accountCustomId,
            scope: response?.data?.scope,
            scopeId: response?.data?.scopeId,
            scopeName: response?.data?.scopeName,
            currency: response?.data?.currency,
            priceComponents: response?.data?.tariffPricingComponent?.map(
              (comp: any, index: number) => ({
                id: index + 1,
                componentType: comp.componentType,
                componentName: comp.componentName,
                valueType: comp.valueType,
                value: comp.value,
                order: comp.order,
              }),
            ),
            timeBasedPricingSlots: (
              response?.data?.timeBasedPricingDetails ||
              response?.data?.timeBasedPricingSlots ||
              []
            ).map((slot: any, index: number) => ({
              id: index + 1,
              name: slot.name,
              startTime: slot.startTime,
              endTime: slot.endTime,
              recurrenceType: slot.recurrenceType,
              applicableOn:
                slot.dates && slot.dates.length > 0
                  ? slot.dates.map((date: string) => ConvertDDMMYYYYtoISO(date)).join(', ')
                  : '-',
              days: Array.isArray(slot.daysOfWeek) ? slot.daysOfWeek.join(' | ') : '-',
              priceMultiplier: String(slot.priceMultiplier),
            })),
          };
          setTariffDetails(output);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [tariffId, isPartner]);

  if (loading) {
    return (
      <div className="tariff-side-pane-wrap">
        <div className="loader-wrap">
          <BatteryLoader />
        </div>
      </div>
    );
  }

  return (
    <div className="tariff-side-pane-wrap">
      <div className="tariff-side-pane-container">
        <div className="details-section">
          <p className="section-title">Basic Details</p>
          <div className="details-grid three-column">
            <div className="detail-item">
              <p className="label">Tariff Name</p>
              <p className="value">{tariffDetails.tariffName || 'N/A'}</p>
            </div>

            <div className="detail-item">
              <p className="label">Tariff Type</p>
              <p className="value">{tariffDetails.tariffType || 'N/A'}</p>
            </div>

            <div className="detail-item">
              <p className="label">Country</p>
              <p className="value">{tariffDetails.country || 'N/A'}</p>
            </div>

            <div className="detail-item">
              <p className="label">State</p>
              <p className="value">{tariffDetails.state || 'N/A'}</p>
            </div>

            <div className="detail-item">
              <p className="label">City</p>
              <p className="value">{tariffDetails.city || 'N/A'}</p>
            </div>

            <div className="detail-item">
              <p className="label">Scope Id</p>
              <p className="value">{tariffDetails.scopeId || 'N/A'}</p>
            </div>

            <div className="detail-item">
              <p className="label">Scope Name</p>
              <p className="value">{tariffDetails.scopeName || 'N/A'}</p>
            </div>

            <div className="detail-item">
              <p className="label">Created By</p>
              <p className="value">{tariffDetails.createdBy || 'N/A'}</p>
            </div>

            <div className="detail-item">
              <p className="label">Edited By</p>
              <p className="value">{tariffDetails.editedBy || 'N/A'}</p>
            </div>
          </div>
        </div>

        <div className="details-section">
          <p className="section-title">Technical Details</p>
          <div className="details-grid two-column">
            <div className="detail-item">
              <p className="label">Current Type</p>
              <p className="value">{tariffDetails.currentType || 'N/A'}</p>
            </div>

            <div className="detail-item">
              <p className="label">Connector Rating (kW)</p>
              <p className="value">
                {[tariffDetails.currentType, tariffDetails.capacityKW, tariffDetails.unit]
                  .filter(Boolean)
                  .join(' ') || 'N/A'}
              </p>
            </div>

            <div className="detail-item">
              <p className="label">Enforcement Date</p>
              <p className="value">{tariffDetails.enforcementDate || 'N/A'}</p>
            </div>

            <div className="detail-item">
              <p className="label">Expiry Date</p>
              <p className="value">{tariffDetails.expiryDate || 'N/A'}</p>
            </div>
          </div>
        </div>

        <div className="details-section">
          <p className="section-title">Price Components</p>
          <div className="simple-table">
            <div className="simple-table-header">
              <div className="col-1-5">Component Type</div>
              <div className="col-1-5">Component Name</div>
              <div className="col-1-5">Value Type</div>
              <div className="col-1">Value</div>
              <div className="col-0-5">Order</div>
            </div>
            {tariffDetails?.priceComponents?.map((comp: any) => (
              <div className="simple-table-row" key={comp.id}>
                <div className="col-1-5">{comp.componentType || 'N/A'}</div>
                <div className="col-1-5">{comp.componentName || 'N/A'}</div>
                <div className="col-1-5">{comp.valueType || 'N/A'}</div>
                <div className="col-1">
                  {comp.value !== undefined && comp.value !== null
                    ? `${Number(comp.value).toFixed(2)} ${tariffDetails.currency || ''}`
                    : 'N/A'}
                </div>
                <div className="col-0-5">{comp.order ?? 'N/A'}</div>
              </div>
            ))}
            {(!tariffDetails?.priceComponents || tariffDetails.priceComponents.length === 0) && (
              <div className="simple-table-row">
                <div className="col-1">No Price Components found</div>
              </div>
            )}
          </div>
        </div>

        {false && (
          <div className="details-section">
            <p className="section-title">Time Based Pricing Slots</p>
            <div className="pricing-slots-list">
              {!tariffDetails?.timeBasedPricingSlots ||
              tariffDetails.timeBasedPricingSlots.length === 0 ? (
                <div className="pricing-slot-item">
                  <div className="details-grid three-column">
                    <div className="detail-item">
                      <p className="label">Slot Name</p>
                      <p className="value">N/A</p>
                    </div>
                    <div className="detail-item">
                      <p className="label">Start Time</p>
                      <p className="value">N/A</p>
                    </div>
                    <div className="detail-item">
                      <p className="label">End Time</p>
                      <p className="value">N/A</p>
                    </div>
                    <div className="detail-item">
                      <p className="label">Recurrence Type</p>
                      <p className="value">N/A</p>
                    </div>
                    <div className="detail-item">
                      <p className="label">Applicable On</p>
                      <p className="value">N/A</p>
                    </div>
                    <div className="detail-item">
                      <p className="label">Days</p>
                      <p className="value">N/A</p>
                    </div>
                    <div className="detail-item">
                      <p className="label">Multiplier</p>
                      <p className="value">N/A</p>
                    </div>
                  </div>
                  <div style={{ marginTop: '24px' }}>
                    <p className="no-data-text">No Time Based Pricing Slots found</p>
                  </div>
                </div>
              ) : (
                tariffDetails.timeBasedPricingSlots.map((slot: any, index: number) => (
                  <div className="pricing-slot-item" key={slot.id}>
                    {index > 0 && <hr className="slot-divider" />}
                    <div className="details-grid three-column">
                      <div className="detail-item">
                        <p className="label">Slot Name</p>
                        <p className="value">{slot.name || 'N/A'}</p>
                      </div>
                      <div className="detail-item">
                        <p className="label">Start Time</p>
                        <p className="value">{slot.startTime || 'N/A'}</p>
                      </div>
                      <div className="detail-item">
                        <p className="label">End Time</p>
                        <p className="value">{slot.endTime || 'N/A'}</p>
                      </div>
                      <div className="detail-item">
                        <p className="label">Recurrence Type</p>
                        <p className="value">{slot.recurrenceType || 'N/A'}</p>
                      </div>
                      <div className="detail-item">
                        <p className="label">Applicable On</p>
                        <p className="value">{slot.applicableOn || 'N/A'}</p>
                      </div>
                      <div className="detail-item">
                        <p className="label">Days</p>
                        <p className="value">{slot.days || 'N/A'}</p>
                      </div>
                      <div className="detail-item">
                        <p className="label">Multiplier</p>
                        <p className="value">{slot.priceMultiplier || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      <div className="sidepanel-footer-actions">
        <MyButton
          label="Verify Tariff"
          buttonType="outline"
          onClickFunc={() => {
            navigate(
              `/ev-admin/tariffs/view/${tariffId}?isExtended=true${isPartner ? '&isPartner=true' : ''}`,
            );
          }}
        />
        <MyButton
          label="Edit Tariff"
          buttonType="submit"
          onClickFunc={() =>
            navigate(
              `/ev-admin/tariffs/edit-tariff/${tariffId}${isPartner ? '?isPartner=true' : ''}`,
            )
          }
          disableButton={
            !permissionCheck('partnerTariffUpdate') && !permissionCheck('cpoTariffUpdate')
          }
        />
      </div>
    </div>
  );
}

TariffSidePanel.defaultProps = {
  fetchIdFrom: 'queryParams',
  id: '',
  isPartner: false,
};

export default TariffSidePanel;
